import { ALL_BLOCKS, changeLogTypes } from 'components/BlocksList/constants';
import { useProcessingQueue } from 'components/BlocksList/hooks/useProcessingQueue';
import {
  exportBlocksToXlsx,
  getDropdownItemsForBlocksPage,
  getRowData,
} from 'components/BlocksList/utilities';
import {
  ORDER_TABS_IDS,
  UNSUBMITTED_ORDERS_TAB_VALUE,
} from 'components/PathologyMap/constants/common';
import { ORDER_NAME_SORT_FIELD } from 'components/SlideGroups/utilities/constants';
import { usePrepareOrderForSelectedTab } from 'components/UserProfile/hooks';
import { buildTeamInfo } from 'components/UserProfile/utilities';
import {
  FETCH_PROJECTS_ERROR_MESSAGE,
  FETCH_TEAMS_ERROR_MESSAGE,
} from 'constants/errorMessages';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetProjectsQuery } from 'store/apis/projectsApi';
import {
  useGetTeamsQuery,
  useLazyGetBlockSlidesQuery,
} from 'store/apis/teamsApi';
import { sortOrder } from 'utilities/constants';
import { useQueryError } from 'utilities/hooks/useQueryError/useQueryError';
import { useTeamBlocks } from 'components/BlocksList/hooks/useTeamBlocks';
import { useLocation } from 'react-router';

export const useBlocksPage = () => {
  const location = useLocation();

  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedBlocksType, setSelectedBlocksType] = useState(
    getDropdownItemsForBlocksPage(ALL_BLOCKS)[0].value,
  );

  const {
    data: projects,
    isLoading: isProjectsLoading,
    error: projectsError,
  } = useGetProjectsQuery();

  const {
    data: teams,
    error,
    isLoading: isTeamsLoading,
  } = useGetTeamsQuery(undefined);
  useQueryError(error, FETCH_TEAMS_ERROR_MESSAGE);
  useQueryError(projectsError, FETCH_PROJECTS_ERROR_MESSAGE);

  const {
    blocks: blocksData,
    isBlocksLoading,
    getTeamBlocks,
  } = useTeamBlocks(selectedTeam, selectedBlocksType);

  const [sort, setSort] = useState({
    field: ORDER_NAME_SORT_FIELD,
    order: sortOrder.DESC,
  });

  const [getBlockSlides] = useLazyGetBlockSlidesQuery();

  const getQuickFilterInitialValue = () => {
    const hash = new URLSearchParams(location.hash);

    return hash.get('#order') || '';
  };

  const [gridAPI, setGridAPI] = useState(null);
  const [quickFilterText, setQuickFilterText] = useState(
    getQuickFilterInitialValue(),
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const isLoading = isBlocksLoading || isTeamsLoading || isProjectsLoading;
  const [isOnlyWithSlides, setIsOnlyWithSlides] = useState(false);
  const [changeLogActive, setChangeLogActive] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const {
    handleAddToQueue,
    checkRowSelectable,
    processingQueue,
    setProcessingQueue,
    handleCreateNewOrder,
    activeTab,
    setActiveTab,
    selectedSamples,
    setSelectedSamples,
    blocksModalOpen,
    handleBlockModalClose,
    getSelectedSamplesNames,
    handleAddToNewOrder,
    handleBlockModalOpen,
  } = useProcessingQueue(selectedRows) || {};

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const blockUUID = searchParams.get('blockUUID');

  useEffect(() => {
    if (blockUUID && blocksData) {
      const block = blocksData.find(({ uuid }) => uuid === blockUUID);
      if (block) {
        setSelectedRows([block]);
      }
    }
  }, [blockUUID, blocksData]);

  const getNodeByUUID = (gridApi, uuid) => {
    let matchingRowNode;

    gridApi.forEachNode((node) => {
      if (node.data?.uuid === uuid) {
        matchingRowNode = node;
      }
    });

    return matchingRowNode;
  };

  const updateSelectedSlides = (selectedBlockUUID, withPrev = false) => {
    getBlockSlides({ blockUUID: selectedBlockUUID })
      .unwrap()
      .then((res) => {
        const fetchedSlides = res.map((slide) => ({
          ...slide,
          blockUUID: selectedBlockUUID,
        }));

        setSelectedSlides((prev) => [
          ...(withPrev ? prev : []),
          ...fetchedSlides,
        ]);
      })
      .catch(() => {
        setSelectedSlides((prev) => (withPrev ? prev : []));
      });
  };

  const handleFirstDataRendered = () => {
    if (blockUUID) {
      const matchingRowNode = getNodeByUUID(gridAPI, blockUUID);
      const parentRowNode = matchingRowNode?.parent;
      if (matchingRowNode) {
        if (parentRowNode) {
          parentRowNode.setExpanded(true);
        }
        matchingRowNode.setSelected(true);
        gridAPI.ensureNodeVisible(matchingRowNode.rowIndex, 'top');
        updateSelectedSlides(blockUUID);
      }
    }
  };

  const onRowGroupOpened = (event) => {
    if (event.node.expanded) {
      const childCount = event.node.childrenAfterFilter.length;
      const index = event.node.rowIndex;
      gridAPI.ensureIndexVisible(index + childCount);
    }
  };

  const { selectedTabSortedOrders: orders, isOrdersLoading } =
    usePrepareOrderForSelectedTab(
      selectedTeam,
      ORDER_TABS_IDS[UNSUBMITTED_ORDERS_TAB_VALUE],
      sort,
    );

  const onGridReady = useCallback(
    (params) => {
      setGridAPI(params.api);

      if (selectedRows) {
        const selectedNodes = selectedRows.map((row) =>
          getNodeByUUID(params.api, row.uuid),
        );

        params.api.forEachNode((node) => {
          if (selectedNodes.includes(node)) {
            node.setSelected(true);
          }
        });
      }
    },
    [setGridAPI, selectedRows],
  );

  useEffect(() => {
    if (selectedTeam || !teams?.length) return;

    setSelectedTeam(teams[0]);
  }, [teams, selectedTeam]);

  useEffect(() => {
    if (!gridAPI || changeLogActive) {
      return;
    }

    if (isLoading) {
      gridAPI.showLoadingOverlay();
    }
  });

  const onQuickFilterText = (event) => {
    setQuickFilterText(event.target.value);
  };

  const onRowSelected = (event) => {
    const { data: { block } = '' } = event.node;
    if (!block) return;

    setSelectedRows(
      gridAPI?.getSelectedNodes()?.map((node) => node.data.block) || [],
    );

    if (event.node.selected) {
      updateSelectedSlides(block.uuid, true);
    } else {
      setSelectedSlides((prev) =>
        prev.filter((slide) => slide.blockUUID !== block.uuid),
      );
    }
  };

  const handleSwitchChange = (event) => {
    setIsOnlyWithSlides(event.target.checked);
  };

  const teamInfo = useMemo(() => buildTeamInfo(selectedTeam), [selectedTeam]);

  const getSelectedBlockTitle = () => {
    const block = gridAPI?.getSelectedNodes()[0]?.data;

    return block?.order?.join(' / ') || '';
  };

  const tableTitle =
    selectedRows.length === 1
      ? getSelectedBlockTitle()
      : `${selectedRows.length} samples selected`;

  const rowData = useMemo(
    () =>
      getRowData(blocksData ?? [], projects).filter(
        (block) => block.atHistowiz,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, blocksData, processingQueue],
  );

  const filteredRowData = useMemo(
    () =>
      isOnlyWithSlides ? rowData.filter((row) => row.slidesCount) : rowData,
    [isOnlyWithSlides, rowData],
  );

  const resetSelectedRows = () => {
    gridAPI?.deselectAll();
    setSelectedRows([]);
    getTeamBlocks({ teamUUID: selectedTeam.uuid });
  };

  const handleMenuClick = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleOpenChangeLog = () => {
    handleMenuClose();
    setChangeLogActive(changeLogTypes.CONFIRMED);
  };

  const handleOpenPending = () => {
    handleMenuClose();
    setChangeLogActive(changeLogTypes.PENDING);
  };

  const handleExportToExcel = () => {
    const teamName = (selectedTeam?.name || 'team')
      .trim()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '');
    const teamId = selectedTeam?.uuid || '';
    const baseName = [teamName, teamId, 'blocks'].filter(Boolean).join('-');

    exportBlocksToXlsx(filteredRowData, baseName);
  };

  const teamBlocksCount = blocksData?.reduce((acc, block) => {
    const teamUuid = block.team.uuid;
    acc[teamUuid] = (acc[teamUuid] || 0) + 1;

    return acc;
  }, {});

  return {
    selectedTeam,
    teamInfo,
    setSelectedTeam,
    teams,
    blocksData,
    tableTitle,
    selectedRows,
    onQuickFilterText,
    handleSwitchChange,
    filteredRowData,
    onGridReady,
    onRowSelected,
    quickFilterText,
    isOnlyWithSlides,
    selectedSlides,
    handleAddToQueue,
    checkRowSelectable,
    processingQueue,
    setProcessingQueue,
    handleCreateNewOrder,
    activeTab,
    setActiveTab,
    selectedSamples,
    setSelectedSamples,
    selectedBlocksType,
    setSelectedBlocksType,
    blocksModalOpen,
    handleBlockModalClose,
    getSelectedSamplesNames,
    handleAddToNewOrder,
    handleBlockModalOpen,
    orders,
    isOrdersLoading,
    sort,
    setSort,
    handleFirstDataRendered,
    onRowGroupOpened,
    resetSelectedRows,
    changeLogActive,
    setChangeLogActive,
    teamUUID: selectedTeam?.uuid,
    projects,
    isLoading,
    handleMenuClick,
    menuAnchor,
    handleMenuClose,
    handleOpenChangeLog,
    handleOpenPending,
    teamBlocksCount,
    handleExportToExcel,
  };
};
